import { AntragMetaData, FormDocument, FormResult, FormValue } from '../model';
import { AntragMetaDataDtoAssemblerService } from '../dto-assembler/antrag-meta-data-dto-assembler.service';
import { ConverterService, EndToEndEncrypter, Guid, StammdatenService } from '@ebeihilfe/core';
import { IElement, IPanel, ISurvey, Model, PanelModel, SurveyModel } from 'survey-core';
import { Injectable, inject, signal } from '@angular/core';

type CustomChoice = IElement & {
  type: string;
  value: string;
};

type CustomElement = IElement & {
  type: string;
  value: string;
  choices: IElement[];
};

@Injectable({
  providedIn: 'root',
})
export class MetadataHelpersService {
  private readonly end2EndEncrypter = inject(EndToEndEncrypter);
  private readonly antragMetaDataDtoAssemblerService = inject(AntragMetaDataDtoAssemblerService);
  private readonly converterService = inject(ConverterService);
  private readonly stammdatenService = inject(StammdatenService);
  public surveyModel = signal(new Model());

  /**
   * Creates a temporary object from the given Survey.js form and state to update metadata in the store.
   * todo Copied and combined from multiple components, should be done in reducer.
   *
   * @param {SurveyModel} surveyModel - The Survey.js form model containing data and its validation state.
   * @param {AntragMetaData} metadata - The existing metadata to update with the new form data.
   * @param {FormDocument} documentForm - The form document containing additional metadata such as form identifier and mandatoriness.
   * @return {AntragMetaData} The updated or newly created temporary metadata object with the form data appended.
   */
  public createTemporaryObjectFromSurveyJsAndStateToUpdateMetadataInStore(
    surveyModel: SurveyModel,
    metadata: AntragMetaData | undefined,
    documentForm: FormDocument,
  ): AntragMetaData {
    const formValueResults: FormValueResultMap[] = [];
    let panelModel = new PanelModel();
    surveyModel.getAllPanels().forEach((panel) => {
      panelModel = panel as PanelModel;
      panelModel.questions.flatMap((question) => {
        const rawValue = question.value;
        const value = typeof rawValue === 'number' ? String(rawValue) : rawValue;

        if (question.getType() === 'checkbox') {
          return question.value.map((name: string) =>
            formValueResults.push({
              key: panelModel.getPropertyValue('WordTemplateName'),
              value: FormValue.create(question.getType(), name, true, question.name),
            }),
          );
        }

        // map radiogroup to checkbox
        if (question.getType() === 'radiogroup') {
          formValueResults.push({
            key: panelModel.getPropertyValue('WordTemplateName'),
            value: FormValue.create('checkbox', question.value, true, question.name),
          });
          return;
        }

        formValueResults.push({
          key: panelModel.getPropertyValue('WordTemplateName'),
          value: FormValue.create(question.getType(), question.name, value),
        });
      });
    });

    const uniqueFormValueResults = [
      ...new Map(
        formValueResults
          .filter((item) => !!item.value.name)
          .map((item) => [`${item.value.$type}-${item.value.name}-${item.value.parent}`, item]),
      ).values(),
    ];

    const formResults = this.createFormResults(
      uniqueFormValueResults,
      documentForm.formId,
      surveyModel.validate(),
      documentForm.isMandatory,
    );

    if (!formResults && uniqueFormValueResults.length === 0) {
      throw new Error('No panel found in survey');
    }
    if (metadata != null) {
      return this.addOrUpdateMetadata(metadata, formResults);
    } else {
      return new AntragMetaData({ formResults: formResults });
    }
  }

  public async deserialize(encryptedMetadata: string): Promise<AntragMetaData> {
    const arrayBuffer = await this.end2EndEncrypter.decryptFromBase64(JSON.stringify(encryptedMetadata));

    if (arrayBuffer == null) {
      throw new Error('Could not decrypt metadata');
    }

    const decryptedMetadataJson = this.converterService.uIntArrayToString(arrayBuffer);

    return this.antragMetaDataDtoAssemblerService.fromDto(JSON.parse(decryptedMetadataJson));
  }

  public extractDefinitionNames(definitions: ISurvey): string[] {
    const names: string[] = [];

    definitions.pages.forEach((page) => {
      page.elements.forEach((el: IPanel | IElement) => {
        const elements = 'elements' in el ? el.elements : [el];
        elements.forEach((item: IElement) => {
          const customElement = item as CustomElement;
          if (customElement.type && customElement.name && 'choices' in customElement) {
            customElement.choices.forEach((choice: IElement) => {
              const customChoice = choice as CustomChoice;
              names.push(customChoice.value);
            });
          } else if (customElement.type && customElement.name) {
            names.push(item.name);
          }
        });
      });
    });

    return names;
  }

  public async serialize(metaData: AntragMetaData): Promise<string> {
    const antragMetaDataDto = this.antragMetaDataDtoAssemblerService.toDto(metaData);

    const serializedMetadata = await this.end2EndEncrypter.encryptToBase64(JSON.stringify(antragMetaDataDto));
    return serializedMetadata ?? '';
  }

  public clearSurveyValueIfPanelIsInvisible(
    survey: Model,
    mode: 'onHidden' | 'onHiddenContainer' = 'onHiddenContainer',
  ): void {
    survey.getAllQuestions().forEach((question) => {
      question.clearIfInvisible = mode;
    });
  }

  private addOrUpdateMetadata(metadata: AntragMetaData, newItems: FormResult[]): AntragMetaData {
    return new AntragMetaData({
      ...metadata,
      formResults: [...newItems],
    });
  }

  private createFormResults(
    formValueMap: FormValueResultMap[],
    formId: string,
    isValid: boolean,
    isMandatory: boolean,
  ): FormResult[] {
    const stammdatenResults = this.prepareStammdatenForSend().filter((stammdatenResult) => !!stammdatenResult.name);

    const groupedResults = formValueMap.reduce(
      (formValueResultMap, { key, value }) => {
        (formValueResultMap[key] ??= []).push(value);
        return formValueResultMap;
      },
      {} as Record<string, FormValue[]>,
    );

    if (Object.keys(groupedResults).length === 0) {
      return [];
    }

    return Object.entries(groupedResults).map(([key, values]) => {
      return new FormResult({
        templateName: key,
        results: [...values, ...stammdatenResults],
        formId: Guid.create(formId),
        isValid: isValid,
        isMandatory: isMandatory ?? false,
        createdAt: new Date(),
      });
    });
  }

  private convertStammDatenKey(key: string): string {
    const keyMapping: { [key: string]: string } = {
      personalnummer: '$$_Personalnummer',
      nachname: '$$_AntragsstellerNachname',
      vorname: '$$_AntragsstellerVorname',
      gebdat: '$$_Geburtsdatum',
      email: '$$_EMail',
    };

    return keyMapping[key] ?? '';
  }

  private prepareStammdatenForSend(): FormValue[] {
    const stammdaten = this.stammdatenService.stammdaten();
    const stammdatenResult: FormValue[] = [];
    Object.keys(stammdaten).forEach((key) => {
      stammdatenResult.push(FormValue.create('text', this.convertStammDatenKey(key), stammdaten[key]));
    });
    return stammdatenResult;
  }
}
type FormValueResultMap = {
  key: string;
  value: FormValue;
};
